import { useEffect, useMemo, useState } from 'react';
import { notebookLinkLabel } from '@harvus/shared';
import { B } from '../constants/brand';
import { api } from '../api/client';
import { Spinner, Icon } from '../components/ui';

// =============================================================
// LinkPicker — "link this note to a record", shared by the Notes app and both
// Quick note surfaces (the pop-up and the Dashboard card). Linking SHARES the
// note with the record's team, which the picker says before anyone picks.
//
// onPick(ref, row): ref is { type, id } (what the API takes); row carries the
// label/detailType/dealName, so a caller holding links before the note exists
// can render a chip without a second request.
// =============================================================

export const linkChipStyle = (type) => {
  const tones = {
    Deal: { bg: '#e6eef7', fg: '#1f3d63' },
    DealDetail: { bg: '#f1ebf8', fg: '#4b2d73' },
    Task: { bg: '#e8f3ee', fg: '#1d5a40' },
  };
  const t = tones[type] || tones.Deal;
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
    padding: '4px 10px', borderRadius: 999, background: t.bg, color: t.fg, border: 0,
  };
};

export function LinkPicker({ existing, onClose, onPick }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const have = useMemo(() => new Set((existing || []).map(l => `${l.type}:${l.id}`)), [existing]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`notebook-link-targets?q=${encodeURIComponent(q)}`);
        if (!cancelled) setItems(res.items || []);
      } catch { if (!cancelled) setItems([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const groups = [
    { type: 'Deal', title: 'Deals' },
    { type: 'DealDetail', title: 'Capital items' },
    { type: 'Task', title: 'Open tasks' },
  ];

  return (
    <div role="dialog" aria-modal="true" aria-label="Link this note"
      style={{ position: 'fixed', inset: 0, background: B.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: B.surface, borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: B.shadowXl }}>
        <div style={{ padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <strong style={{ fontSize: 16 }}>Link this note</strong>
            <button type="button" aria-label="Close" onClick={onClose} style={{ border: 0, background: 'transparent', cursor: 'pointer', color: B.textMd }}><Icon name="close" size={18} /></button>
          </div>
          <span style={{ fontSize: 13, color: B.textMd }}>Everyone who can see the record you pick will be able to read and edit this note.</span>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search deals, capital items, tasks"
            aria-label="Search records"
            style={{ padding: '9px 12px', borderRadius: 10, border: `1px solid ${B.border}`, font: 'inherit', fontSize: 14 }} />
        </div>
        <div style={{ overflowY: 'auto', padding: '0 8px 12px' }}>
          {loading && <div style={{ padding: 12 }}><Spinner /></div>}
          {!loading && groups.map(g => {
            const rows = items.filter(i => i.type === g.type);
            if (!rows.length) return null;
            return (
              <div key={g.type}>
                <div style={{ padding: '10px 8px 4px', fontSize: 12, fontWeight: 700, color: B.textMd }}>{g.title}</div>
                {rows.map(r => {
                  const linked = have.has(`${r.type}:${r.id}`);
                  return (
                    <button key={r.id} type="button" disabled={linked} onClick={() => onPick({ type: r.type, id: r.id }, r)}
                      style={{ display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left', padding: '8px 10px', border: 0, borderRadius: 8, background: 'transparent', cursor: linked ? 'default' : 'pointer', opacity: linked ? 0.5 : 1 }}>
                      <span style={{ fontSize: 14, color: B.text }}>{r.label}{linked ? ' · linked' : ''}</span>
                      {(r.dealName || r.detailType) && r.type !== 'Deal' && (
                        <span style={{ fontSize: 12, color: B.textMd }}>{[r.detailType, r.dealName].filter(Boolean).join(' · ')}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {!loading && !items.length && <div style={{ padding: 16, fontSize: 13, color: B.textMd }}>Nothing matches.</div>}
        </div>
      </div>
    </div>
  );
}


// The "Linked to" row under a quick note: a chip per linked record (with ×)
// and a "+ Tag deal" button that opens the picker.
export function NoteLinkBar({ links, onAdd, onRemove, busy }) {
  const [picking, setPicking] = useState(false);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, padding: '8px 14px', borderTop: `1px solid ${B.divider || B.border}` }}>
      {links.length === 0 && <span style={{ fontSize: 12, color: B.textLt }}>Private — tag a deal to share it with that deal's team</span>}
      {links.map(l => (
        <span key={`${l.type}:${l.id}`} style={linkChipStyle(l.type)}>
          {notebookLinkLabel(l)}
          <button type="button" aria-label={`Remove link to ${l.label || 'record'}`} disabled={busy} onClick={() => onRemove(l)}
            style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <Icon name="close" size={12} />
          </button>
        </span>
      ))}
      <button type="button" disabled={busy} onClick={() => setPicking(true)}
        style={{ height: 26, padding: '0 10px', border: `1px dashed ${B.border}`, borderRadius: 999, background: 'transparent', fontSize: 12, color: B.textMd, cursor: 'pointer', fontFamily: 'inherit' }}>
        + Tag deal
      </button>
      {picking && (
        <LinkPicker existing={links} onClose={() => setPicking(false)}
          onPick={(ref, row) => { setPicking(false); onAdd({ ...ref, label: row?.label, detailType: row?.detailType, dealName: row?.dealName }); }} />
      )}
    </div>
  );
}
